import { useEffect, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { BookingDrawer } from '@/components/booking/BookingDrawer';
import * as bookingApi from '@/services/bookingApi';
import type { Booking } from '@/types';

type ViewMode = 'day' | 'week';

function toDateString(date: Date): string {
  return date.toISOString().split('T')[0];
}

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function formatDayLabel(date: Date): string {
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(booking: Booking): string {
  if (!booking.booking_time) return '';
  const [hours, minutes] = booking.booking_time.split(':');
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

export default function SchedulePage() {
  const [viewMode, setViewMode] = useState<ViewMode>('week');
  const [anchorDate, setAnchorDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

  const range = viewMode === 'day'
    ? { from: toDateString(anchorDate), to: toDateString(anchorDate) }
    : (() => {
        const weekStart = startOfWeek(anchorDate);
        const weekEnd = addDays(weekStart, 6);
        return { from: toDateString(weekStart), to: toDateString(weekEnd) };
      })();

  const fetchBookings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await bookingApi.getProviderBookings({
        from: range.from,
        to: range.to,
        per_page: 200,
      });
      setBookings(result.data || []);
    } catch (err: unknown) {
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to load schedule'
      );
    } finally {
      setIsLoading(false);
    }
  }, [range.from, range.to]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const goPrev = () => {
    setAnchorDate((d) => addDays(d, viewMode === 'day' ? -1 : -7));
  };

  const goNext = () => {
    setAnchorDate((d) => addDays(d, viewMode === 'day' ? 1 : 7));
  };

  const goToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setAnchorDate(today);
  };

  const weekDays = viewMode === 'week'
    ? Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchorDate), i))
    : [anchorDate];

  const bookingsByDate = bookings.reduce<Record<string, Booking[]>>((acc, booking) => {
    const key = booking.booking_date?.split('T')[0] ?? booking.booking_date;
    if (!acc[key]) acc[key] = [];
    acc[key].push(booking);
    return acc;
  }, {});

  Object.values(bookingsByDate).forEach((list) => {
    list.sort((a, b) => a.booking_time.localeCompare(b.booking_time));
  });

  const statusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending_verification':
      case 'pending_confirmation':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
      case 'rejected':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'no_show':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-muted text-foreground border-border';
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Schedule</h1>
          <p className="text-muted-foreground text-sm">
            {viewMode === 'day'
              ? formatDayLabel(anchorDate)
              : `${formatDayLabel(startOfWeek(anchorDate))} – ${formatDayLabel(addDays(startOfWeek(anchorDate), 6))}`}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border bg-card p-1">
            <button
              type="button"
              onClick={() => setViewMode('day')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                viewMode === 'day' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
            >
              Day
            </button>
            <button
              type="button"
              onClick={() => setViewMode('week')}
              className={`px-3 py-1.5 text-sm rounded-md ${
                viewMode === 'week' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'
              }`}
            >
              Week
            </button>
          </div>
          <Button variant="outline" size="sm" onClick={goPrev}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={goNext}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : viewMode === 'week' ? (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
          {weekDays.map((day) => {
            const key = toDateString(day);
            const dayBookings = bookingsByDate[key] || [];
            const isToday = toDateString(new Date()) === key;

            return (
              <div
                key={key}
                className={`min-h-[200px] rounded-xl border bg-card p-3 ${
                  isToday ? 'ring-2 ring-primary/30' : ''
                }`}
              >
                <div className="text-sm font-semibold text-foreground mb-3">
                  {formatDayLabel(day)}
                </div>
                <div className="space-y-2">
                  {dayBookings.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No bookings</p>
                  ) : (
                    dayBookings.map((booking) => (
                      <button
                        key={booking.id}
                        type="button"
                        onClick={() => setSelectedBooking(booking)}
                        className={`w-full text-left p-2 rounded-lg border text-xs ${statusColor(booking.status)}`}
                      >
                        <div className="font-medium">{formatTime(booking)}</div>
                        <div className="truncate">{booking.customer_name}</div>
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border bg-card divide-y">
          {(bookingsByDate[toDateString(anchorDate)] || []).length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No bookings for this day</div>
          ) : (
            (bookingsByDate[toDateString(anchorDate)] || []).map((booking) => (
              <button
                key={booking.id}
                type="button"
                onClick={() => setSelectedBooking(booking)}
                className="w-full flex items-center justify-between p-4 hover:bg-muted/50 text-left"
              >
                <div>
                  <div className="font-medium text-foreground">{booking.customer_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {formatTime(booking)} · {booking.services?.map((s) => s.name).join(', ')}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${statusColor(booking.status)}`}>
                  {booking.status.replace(/_/g, ' ')}
                </span>
              </button>
            ))
          )}
        </div>
      )}

      <BookingDrawer
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
        onUpdated={() => {
          fetchBookings();
          setSelectedBooking(null);
        }}
      />
    </div>
  );
}
